import { useEffect, useState } from 'react';
import validator from 'validator';

import Logger from 'utils/Logger';

/* Inspired by https://medium.com/code-monkey/client-side-form-validation-in-react-40e367de47ba */

export type ValidationRuleConfig<TParams> = Readonly<{
  field: string;
  preset: ValidationRuleDefinition;
  shouldIgnore?: (params: TParams) => boolean;
}>;

export type FormValidatorConfig<TParams> = ReadonlyArray<ValidationRuleConfig<TParams>>;

export type ValidationRuleDefinition = Readonly<
  { // Base properties
    validWhen: boolean;
    message: string;
    args?: any[];
  } & (
    { // Custom validator
      customValidatorFunction: (...args: any[]) => boolean;
      validatorFunction?: never;
    } | { // Built-in validator
      customValidatorFunction?: never;
      validatorFunction: (typeof validator)[keyof typeof validator];
    }
  )
>;

export type ValidationRule<TParams> = ValidationRuleDefinition & {
  field: string;
  shouldIgnore?: (params: TParams) => boolean;
};

export interface FieldValidation {
  isValid: boolean;
  isChecked?: boolean;
  message: string;
}

/**
 * Create a validation rule from a preset rule
 * @param rule - The preset rule to create a validation rule from
 * @param field - The key (in state) of the field to validate
 * @returns A validation rule
 *
 * @example
 * const formValidator = new FormValidator([
 *   createValidationRule(ValidationRulePreset.email, 'email'),
 *   createValidationRule(ValidationRulePreset.password, 'password'),
 * ]);
 */
export const createValidationRule = <TParams>(config: ValidationRuleConfig<TParams>) => ({
  ...config.preset,
  field: config.field,
  shouldIgnore: config.shouldIgnore,
} as ValidationRule<TParams>);

const getEmptyValidation = (): FieldValidation => ({ isValid: true, isChecked: false, message: '' });

/**
 * Built-in validator methods: https://github.com/validatorjs/validator.js/blob/master/README.md#validators
 *
 * @example
 *
 * const validator = new FormValidator([
 *   {
 *     field: 'email',
 *     method: validator.isEmail,
 *     args: [],
 *     validWhen: true,
 *     message: 'Please enter a valid email address.'
 *   },
 *   {
 *     field: 'age',
 *     method: validator.isInt,
 *     args: [{min: 21, max: 65}],  // an array of additional arguments
 *     validWhen: true,
 *     message: 'Your age must be an integer between 21 and 65'
 *   }
 * ])
 *
 * const state =  { email: 'loony tunes', age: 19}
 * const validation = validator.validate(state);
 *
 * {
 *   allValid: false,
 *   email: {
 *     isValid: false,
 *     isChecked: true,
 *     message: 'Please enter a valid email address.'
 *   },
 *   age: {
 *     isValid: false,
 *     isChecked: true,
 *     message: 'Your age must be an integer between 21 and 65'
 *   }
}
 */
export class FormValidator<TParams> {
  #validationRules: ValidationRule<TParams>[];

  constructor(validationRuleConfigs: FormValidatorConfig<TParams>) {
    // validations is an array of rules specific to a form
    this.#validationRules = validationRuleConfigs
      .map(createValidationRule)
      .filter(Boolean) as ValidationRule<TParams>[];
  }

  validate(state: Record<string, any>, params: TParams) {
    const validityResponse = this.#validationRules.reduce((acc, validationRule) => {
      const { field } = validationRule;

      // if the field isn't already marked invalid by an earlier rule
      // this is to make sure we don't mark an invalid field as valid
      if (acc[field]!.isChecked && !acc[field]!.isValid) {
        return acc;
      }

      acc[field] = this.#validateRule(validationRule, state, params);

      return acc;
    }, this.#createValidityResponse());

    const allValid = Object.values(validityResponse).every(({ isValid }) => isValid);

    return {
      allValid,
      ...validityResponse,
    };
  }

  #validateRule(rule: ValidationRule<TParams>, state: Record<string, any>, params: TParams): FieldValidation {
    const {
      validatorFunction,
      customValidatorFunction,
      validWhen,
      message,
      shouldIgnore,
    } = rule;

    if (validatorFunction && (customValidatorFunction as any)) {
      throw new Error('Only one of validatorFunction or customValidatorFunction can be provided');
    }

    if (!validatorFunction && !customValidatorFunction) {
      throw new Error('Either validatorFunction or customValidatorFunction must be provided');
    }

    if (shouldIgnore?.(params)) {
      return getEmptyValidation();
    }

    const validatorResult = this.#executeValidator(rule, state);
    const isValid = validatorResult === validWhen;

    return {
      isChecked: true,
      isValid,
      message: isValid ? '' : message,
    };
  }

  #executeValidator(rule: ValidationRule<TParams>, state: Record<string, any>) {
    const { field, validatorFunction, customValidatorFunction, args = [] } = rule;
    // Validator expects strings, so we convert the field value to a string
    if (typeof validatorFunction === 'function') {
      const stringifiedFieldValue = (state[field] ?? '').toString();
      // The union type from validator is too strict, we need to loosen it a bit
      const typedValidatorFunction = validatorFunction as (...args: any[]) => boolean;
      return typedValidatorFunction(stringifiedFieldValue, ...args);
    }

    // A custom validator can use any value
    if (typeof customValidatorFunction === 'function') {
      return customValidatorFunction(state[field]);
    }

    // Should never happen because we require either validatorFunction or customValidatorFunction
    Logger.error('Validator function not found', { rule });
    return false;
  }

  /**
   * Creates a validity response object with all fields set to empty validation
   * @example
   * {
   *   isValid: true,
   *   email: { isValid: true, isChecked: false, message: '' },
   *   password: { isValid: true, isChecked: false, message: '' },
   * }
   */
  #createValidityResponse() {
    const fieldValidations = this.#validationRules.reduce((acc, rule) => {
      acc[rule.field] = getEmptyValidation();
      return acc;
    }, {} as Record<ValidationRule<TParams>['field'], FieldValidation>);

    return fieldValidations;
  }
}

export const createImmutableFormValidatorConfig = <TParams>(validationRuleConfigs: FormValidatorConfig<TParams>) => {
  const frozenConfigs = validationRuleConfigs.map((config) => Object.freeze(config));
  return Object.freeze(frozenConfigs);
};

/**
 * Creates a validator instance from an array of validation rule configs
 * The configs passed in must be immutable.
 * Use `createImmutableFormValidatorConfig` to create immutable configs.
 *
 * @param validationRuleConfigs - An array of validation rule configs
 * @returns A validator instance
 */
export const useFormValidator = <TParams>(validationRuleConfigs: FormValidatorConfig<TParams>) => {
  const [validator, setValidator] = useState(new FormValidator(validationRuleConfigs));

  useEffect(() => {
    setValidator(new FormValidator(validationRuleConfigs));
  }, [validationRuleConfigs]);

  return validator;
};
